import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle2, MessageSquare, ChevronLeft, ChevronRight,
  BookOpen, Music, Sparkles, Scroll, Anchor, Heart, Compass, Lightbulb, Target, Telescope,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import PageSubHeader from '@/components/PageSubHeader';
import { useSafeBack } from '@/utils/useSafeBack';
import { API_URL as API } from '@/utils/apiConfig';
import useCachedFetch from '@/hooks/useCachedFetch';
import PlanCompletionModal from '@/components/PlanCompletionModal';

import { useTranslation } from 'react-i18next';

const ICONS = { BookOpen, Music, Sparkles, Scroll, Anchor, Heart };

// Tiny markdown helper: turns **text** into <strong>text</strong>, escaping the rest.
function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
function renderInlineBold(text) {
  const escaped = escapeHtml(text);
  return escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

/** Split a markdown-ish block into `<p>` paragraphs with inline
 *  bold / italic preserved. Keeps things intentionally simple —
 *  the LLM-generated study copy never contains lists or headings. */
function renderParagraphs(text) {
  if (!text) return '';
  return text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p style="margin:0 0 0.75em 0;">${renderInlineBold(p).replace(/\n/g, '<br />')}</p>`)
    .join('');
}

export default function StudyPlanDayPage() {
  const {
    t,
    i18n
  } = useTranslation();

  const { planId, dayNum } = useParams();
  const dayNumber = parseInt(dayNum, 10);
  const navigate = useNavigate();
  const goBack = useSafeBack(`/study-plans/${planId}`);
  const [marking, setMarking] = useState(false);
  const [togglingPassage, setTogglingPassage] = useState(null);
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const lang = (i18n.language || 'en').split('-')[0];

  // Cached fetch — reads the last snapshot from localStorage
  // instantly (no pop-in on remount / cold app relaunch) and
  // revalidates in background. Cold-launch (first-ever visit) falls
  // through to the loading branch below.
  const fetcher = useCallback(async () => {
    const res = await axios.get(`${API}/reading-plans/${planId}`, {
      params: { locale: lang },
      withCredentials: true,
    });
    return { plan: res.data?.plan, progress: res.data?.progress };
  }, [planId, lang]);
  const {
    data,
    isColdLoading,
    error: fetchError,
    mutate,
  } = useCachedFetch(planId ? `plan:${planId}:${lang}` : null, fetcher);
  const plan = data?.plan || null;
  const progress = data?.progress || null;

  // If the fetch failed AND we have nothing cached, bounce back to
  // the plan detail page (same UX as before the cache migration).
  if (fetchError && !data) {
    toast.error(t('generated.studyplandaypage_failed_to_load_d823'));
    navigate(`/study-plans/${planId}`, { replace: true });
  }

  // Per-passage read tracking — derived from progress.passage_progress.
  // Shape on backend: { "<dayNumber>": [<passageIdx>, ...] }
  const readPassages = useMemo(() => {
    const arr = progress?.passage_progress?.[String(dayNumber)] || [];
    return new Set(arr);
  }, [progress, dayNumber]);
  const readPassageCount = readPassages.size;

  const togglePassageRead = async (idx) => {
    if (!progress) {
      toast.error(t('generated.studyplandaypage_start_the_plan_6ed0'));
      return;
    }
    setTogglingPassage(idx);
    try {
      const res = await axios.post(
        `${API}/reading-plans/${planId}/days/${dayNumber}/passages/${idx}/read`,
        {},
        { withCredentials: true },
      );
      // Optimistically write the fresh progress into the cache so the
      // next render reflects the toggle immediately AND survives a
      // remount/relaunch.
      mutate((prev) => ({ ...(prev || {}), progress: res.data?.progress }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    } finally {
      setTogglingPassage(null);
    }
  };

  const day = useMemo(() => {
    if (!plan) return null;
    return plan.days.find((d) => d.day_number === dayNumber);
  }, [plan, dayNumber]);

  const cover = plan?.cover_color || 'var(--theme-accent)';
  const Icon = ICONS[plan?.icon] || BookOpen;
  const isComplete = progress?.completed_days?.includes(dayNumber);

  const markComplete = async () => {
    setMarking(true);
    try {
      const res = await axios.post(
        `${API}/reading-plans/${planId}/days/${dayNumber}/complete`,
        {},
        { withCredentials: true },
      );
      mutate((prev) => ({ ...(prev || {}), progress: res.data?.progress }));
      toast.success(`Day ${dayNumber} complete!`);
      // If there's a next day, advance automatically; otherwise back to plan
      const nextDay = res.data?.progress?.current_day;
      const finished = !!res.data?.progress?.completed_at;
      if (finished) {
        // Celebrate before navigating away. Modal takes over — user
        // closes it to go home, or taps Share / Browse more.
        setCelebrationOpen(true);
      } else if (nextDay && nextDay !== dayNumber) {
        // Stay on this day so user can scroll back, but show next-day button
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally {
      setMarking(false);
    }
  };

  const discussWithEili = () => {
    // Seed a chat with the day's passage references and AI context.
    // We use the location.state seedMessage path (same as DYK's
    // "Discuss with Eili") so ChatPage creates a fresh conversation
    // and pre-fills the input — the older `?seed=` query-param path
    // just set input state without opening a conversation, which
    // left the user staring at an empty "no conversation selected"
    // pane with no visible input to send.
    const passages = (day?.passages || []).map((p) => p.label).join(', ');
    const seed = day?.ai_context_seed
      ? `${day.ai_context_seed}\n\nMy reading: ${passages}.\n\nDiscuss this with me.`
      : `I'm on Day ${dayNumber} of ${plan.title}, reading ${passages}. Help me understand the historical context and key themes.`;
    navigate('/chat', {
      state: {
        seedMessage: seed,
        suggestedQuestions: [],
        source: 'study_plan',
      },
    });
  };

  const goDeeperWithEili = () => {
    const passages = (day?.passages || []).map((p) => p.label).join(', ');
    const seed = `I'm on Day ${dayNumber} of "${plan.title}" (reading ${passages}). Help me go deeper on this:\n\n${day.deeper_study}`;
    navigate('/chat', { state: { seedMessage: seed, autoSend: true, source: 'study_plan_deeper' } });
  };

  if (isColdLoading) {
    return (
      <div className="page-container flex items-center justify-center" style={{ background: 'var(--theme-background)' }}>
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
      </div>
    );
  }
  if (!plan || !day) {
    return (
      <div className="page-container flex items-center justify-center" style={{ background: 'var(--theme-background)' }}>
        <p style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.studyplandaypage_day_not_found_cf2f')}</p>
      </div>
    );
  }

  const hasPrev = dayNumber > 1;
  const hasNext = dayNumber < plan.length_days;

  return (
    <div className="page-container" style={{ background: 'var(--theme-background)' }}>
      <Header />
      <PageSubHeader
        onBack={goBack}
        title={day.title}
        subtitle={`${plan.title} · Day ${dayNumber} of ${plan.length_days}`}
        backTestId="day-back"
        rightSlot={(
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: `${cover}22` }}
          >
            <Icon className="h-4 w-4" style={{ color: cover }} />
          </div>
        )}
      />
      <div className="page-content max-w-2xl lg:max-w-4xl mx-auto px-4 lg:px-8 pt-4 lg:pt-8 pb-32 w-full">
        {/* Title */}
        <h1 className="text-xl font-bold mb-3 break-words" style={{ color: 'var(--theme-text)' }} data-testid="day-title">
          {day.title}
        </h1>

        {/* Intro — sets the stage */}
        {day.intro && (
          <div className="mb-6" data-testid="day-intro">
            <p
              className="text-[15px] leading-relaxed"
              style={{ color: 'var(--theme-text)', opacity: 0.9 }}
            >
              {day.intro}
            </p>
          </div>
        )}

        {/* Passages */}
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="h-4 w-4" style={{ color: cover }} />
          <h2 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
            {t('generated.studyplandaypage_today_s_reading_8476')}
          </h2>
          {day.passages.length > 1 && (
            <span className="text-xs ml-auto" style={{ color: 'var(--theme-textSecondary)' }}>
              {readPassageCount} of {day.passages.length} read
            </span>
          )}
        </div>
        <div className="space-y-2 mb-6" data-testid="day-passages">
          {day.passages.map((p, i) => {
            const isRead = readPassages.has(i);
            const verse = p.verse_start
              ? (p.verse_end ? `${p.verse_start}-${p.verse_end}` : String(p.verse_start))
              : '';
            return (
              <div
                key={i}
                className="rounded-xl flex items-center gap-2 transition-all"
                style={{
                  background: isRead ? `${cover}14` : 'var(--glass-bg)',
                  border: `1px solid ${isRead ? `${cover}55` : 'var(--glass-border)'}`,
                  WebkitTapHighlightColor: 'transparent',
                }}
                data-testid={`day-passage-${i}`}
              >
                {/* Tap area: opens Bible reader scoped to this passage */}
                <button
                  onClick={() => {
                    navigate('/bible', {
                      state: {
                        targetReference: { book: p.book, chapter: p.chapter, verse },
                      },
                    });
                  }}
                  className="flex-1 min-w-0 flex items-center gap-3 p-4 text-left active:scale-[0.99] transition-transform"
                  data-testid={`day-passage-${i}-open`}
                  aria-label={`Read ${p.label}`}
                >
                  <BookOpen className="h-4 w-4 shrink-0" style={{ color: cover }} />
                  <span
                    className="text-sm font-medium truncate"
                    style={{
                      color: 'var(--theme-text)',
                      textDecoration: isRead ? 'line-through' : 'none',
                      opacity: isRead ? 0.7 : 1,
                    }}
                  >
                    {p.label}
                  </span>
                  <ChevronRight className="h-4 w-4 ml-auto shrink-0" style={{ color: 'var(--theme-textSecondary)' }} />
                </button>

                {/* Toggle: marks this passage read/unread */}
                <button
                  onClick={() => togglePassageRead(i)}
                  disabled={togglingPassage === i || !progress}
                  className="shrink-0 mr-3 h-8 w-8 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
                  style={{
                    background: isRead ? cover : 'transparent',
                    border: `2px solid ${isRead ? cover : 'var(--glass-border)'}`,
                  }}
                  data-testid={`day-passage-${i}-toggle`}
                  aria-label={isRead ? `Mark ${p.label} unread` : `Mark ${p.label} read`}
                  title={progress ? (isRead ? 'Mark unread' : 'Mark as read') : 'Start the plan to track readings'}
                >
                  {togglingPassage === i ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: isRead ? '#fff' : cover }} />
                  ) : isRead ? (
                    <CheckCircle2 className="h-4 w-4" style={{ color: '#fff' }} />
                  ) : null}
                </button>
              </div>
            );
          })}
        </div>

        {/* Passage breakdown — depth for custom plans, walking the
            reader through what they just read (verse flow, context,
            what each part is actually saying). Only rendered when
            the LLM populated the field so curated plans (which use
            the older schema) still look identical. */}
        {day.passage_breakdown && (
          <div className="mb-6" data-testid="day-passage-breakdown">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="h-4 w-4" style={{ color: cover }} />
              <h2 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                What you just read
              </h2>
            </div>
            <div
              className="text-[15px] leading-relaxed"
              style={{ color: 'var(--theme-text)', opacity: 0.9 }}
              dangerouslySetInnerHTML={{ __html: renderParagraphs(day.passage_breakdown) }}
            />
          </div>
        )}

        {/* Key Themes */}
        {day.key_themes && day.key_themes.length > 0 && (
          <div className="mb-6" data-testid="day-key-themes">
            <div className="flex items-center gap-2 mb-3">
              <Compass className="h-4 w-4" style={{ color: cover }} />
              <h2 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                {t('generated.studyplandaypage_key_themes_f100')}
              </h2>
            </div>
            <ul className="space-y-2">
              {day.key_themes.map((theme, i) => (
                <li
                  key={i}
                  className="flex gap-3 text-[14px] leading-relaxed"
                  style={{ color: 'var(--theme-text)', opacity: 0.9 }}
                >
                  <span
                    className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: cover }}
                  />
                  <span dangerouslySetInnerHTML={{ __html: renderInlineBold(theme) }} />
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Objective connection — ties today's passage to the
            reader's stated end goal. Only present on custom study
            plans (curated plans skip this field). */}
        {day.objective_connection && (
          <div
            className="rounded-2xl p-5 mb-6"
            data-testid="day-objective-connection"
            style={{
              background: `${cover}0d`,
              border: `1px dashed ${cover}55`,
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4" style={{ color: cover }} />
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: cover }}>
                How this connects to your goal
              </p>
            </div>
            <p
              className="text-[14px] leading-relaxed"
              style={{ color: 'var(--theme-text)', opacity: 0.92 }}
            >
              {day.objective_connection}
            </p>
          </div>
        )}

        {/* Reflect — combined contemplative prompt + practical application */}
        {(day.reflection_prompt || day.application) && (
          <div
            className="rounded-2xl p-5 mb-6"
            style={{
              background: `${cover}10`,
              border: `1px solid ${cover}33`,
            }}
            data-testid="day-reflection-prompt"
          >
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: cover }}>
              {t('generated.studyplandaypage_reflect_5ac9')}
            </p>
            {day.reflection_prompt && (
              <p
                className="text-[15px] leading-relaxed mb-4"
                style={{ color: 'var(--theme-text)' }}
              >
                {day.reflection_prompt}
              </p>
            )}
            {day.application && (
              <div
                className="flex gap-3 pt-4"
                style={{ borderTop: `1px solid ${cover}22` }}
                data-testid="day-application"
              >
                <Lightbulb className="h-4 w-4 mt-0.5 shrink-0" style={{ color: cover }} />
                <p
                  className="text-[14px] leading-relaxed"
                  style={{ color: 'var(--theme-text)', opacity: 0.92 }}
                >
                  <span className="font-semibold" style={{ color: cover }}>{t('generated.studyplandaypage_try_this_9520')} </span>
                  {day.application}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Go deeper — optional cross-reference or follow-up question
            for readers who want to push past the daily reading. */}
        {day.deeper_study && (
          <button
            type="button"
            onClick={goDeeperWithEili}
            className="group w-full text-left rounded-2xl p-4 mb-6 flex gap-3 transition-[transform,box-shadow] hover:-translate-y-[1px] hover:shadow-lg active:scale-[0.99]"
            data-testid="day-deeper-study"
            aria-label="Go deeper with Eili"
            style={{
              background: 'var(--glass-bg)',
              border: `1px solid var(--glass-border)`,
            }}
          >
            <Telescope className="h-4 w-4 mt-0.5 shrink-0" style={{ color: cover }} />
            <div className="flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: cover }}>
                Go deeper
              </p>
              <p className="text-[13px] leading-relaxed" style={{ color: 'var(--theme-text)', opacity: 0.9 }}>
                {day.deeper_study}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 mt-0.5 shrink-0 opacity-50 transition-[opacity,transform] group-hover:opacity-100 group-hover:translate-x-0.5" style={{ color: cover }} />
          </button>
        )}

        {/* Discuss with Eili */}
        <Button
          onClick={discussWithEili}
          variant="outline"
          className="w-full rounded-2xl py-5 mb-3 gap-2"
          style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
          data-testid="day-discuss-btn"
        >
          <MessageSquare className="h-4 w-4" style={{ color: cover }} />
          {t('generated.studyplandaypage_discuss_today_s_7471')}
        </Button>

        {/* Mark complete */}
        <Button
          onClick={markComplete}
          disabled={marking || isComplete}
          className="w-full rounded-2xl py-6 text-base font-semibold gap-2"
          style={{
            background: isComplete ? `${cover}33` : cover,
            color: isComplete ? cover : '#fff',
          }}
          data-testid="day-complete-btn"
        >
          {marking ? <Loader2 className="h-4 w-4 animate-spin" />
            : isComplete ? <><CheckCircle2 className="h-4 w-4" />{t('generated.studyplandaypage_day_complete_b2a3')}</>
            : <>{t('generated.studyplandaypage_mark_day_33c9')} {dayNumber} {t('generated.studyplandaypage_complete_1de0')}</>}
        </Button>

        {/* Prev / next nav */}
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasPrev}
            onClick={() => navigate(`/study-plans/${planId}/day/${dayNumber - 1}`)}
            className="gap-1"
            style={{ color: 'var(--theme-textSecondary)', opacity: hasPrev ? 1 : 0.4 }}
            data-testid="day-prev-btn"
          >
            <ChevronLeft className="h-4 w-4" />
            {t('generated.studyplandaypage_day_17c4')} {dayNumber - 1}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasNext}
            onClick={() => navigate(`/study-plans/${planId}/day/${dayNumber + 1}`)}
            className="gap-1"
            style={{ color: 'var(--theme-textSecondary)', opacity: hasNext ? 1 : 0.4 }}
            data-testid="day-next-btn"
          >
            {t('generated.studyplandaypage_day_17c4')} {dayNumber + 1}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <PlanCompletionModal
        open={celebrationOpen}
        onClose={() => {
          setCelebrationOpen(false);
          navigate(`/study-plans/${planId}`, { replace: true });
        }}
        planTitle={plan?.title}
        lengthDays={plan?.length_days}
        cover={cover}
        canShare={!!plan?.is_custom}
        onShare={async () => {
          try {
            const res = await axios.post(
              `${API}/reading-plans/custom/${planId}/share`,
              {},
              { withCredentials: true }
            );
            const url = `${window.location.origin}/api/og/plans/join/${res.data.share_code}`;
            const text = `I just finished "${plan.title}" — a study plan I built with Eili. Want to try it?\n\n${url}`;
            if (navigator.share) {
              await navigator.share({ title: plan.title, text, url });
            } else {
              await navigator.clipboard.writeText(url);
              toast.success('Share link copied to clipboard');
            }
          } catch (err) {
            if (err?.name !== 'AbortError') {
              toast.error(err?.response?.data?.detail || 'Could not create share link');
            }
          }
        }}
        onBrowseMore={() => {
          setCelebrationOpen(false);
          navigate('/study-plans', { replace: true });
        }}
        onViewRecap={plan?.is_custom ? () => {
          setCelebrationOpen(false);
          navigate(`/study-plans/${planId}/recap`, { replace: true });
        } : undefined}
      />
    </div>
  );
}
